import React, { useEffect, useMemo, useState } from 'react';
import SmartDateInput from '../ui/SmartDateInput';

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const addCalendarDays = (value, days) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const oneYearTermEnd = (startValue) => {
  if (!startValue) return '';
  const start = new Date(`${startValue}T12:00:00`);
  if (Number.isNaN(start.getTime())) return '';
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  end.setDate(end.getDate() - 1);
  return toDateInputValue(end);
};

const toTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const LeaseRenewalForm = ({ currentLease, tenant, onSubmit, onCancel, isSubmitting = false }) => {
  const suggestedStart = useMemo(
    () => currentLease?.endDate ? addCalendarDays(currentLease.endDate, 1) : '',
    [currentLease?.endDate]
  );
  const [formData, setFormData] = useState({
    startDate: suggestedStart,
    endDate: oneYearTermEnd(suggestedStart),
    weeklyRent: Number(currentLease?.weeklyRent || 0),
    rentBasis: currentLease?.rentBasis || 'weekly',
    paymentFrequency: currentLease?.paymentFrequency || 'weekly',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData({
      startDate: suggestedStart,
      endDate: oneYearTermEnd(suggestedStart),
      weeklyRent: Number(currentLease?.weeklyRent || 0),
      rentBasis: currentLease?.rentBasis || 'weekly',
      paymentFrequency: currentLease?.paymentFrequency || 'weekly',
      notes: ''
    });
    setErrors({});
  }, [currentLease?.id, currentLease?.weeklyRent, currentLease?.rentBasis, currentLease?.paymentFrequency, suggestedStart]);

  const handleChange = (event) => {
    const { name, value, type } = event.target;
    setFormData(previous => {
      const next = { ...previous, [name]: type === 'number' ? value : value };
      if (name === 'startDate' && value) next.endDate = oneYearTermEnd(value);
      return next;
    });
    setErrors(previous => ({ ...previous, [name]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    const start = toTimestamp(formData.startDate);
    const end = toTimestamp(formData.endDate);
    const currentEnd = Number(currentLease?.endDate || 0);
    if (start === null) nextErrors.startDate = 'Next lease start date is required.';
    if (end === null) nextErrors.endDate = 'Next lease end date is required.';
    if (start !== null && currentEnd && start <= currentEnd) nextErrors.startDate = 'Next lease must start after the current lease ends.';
    if (start !== null && end !== null && end < start) nextErrors.endDate = 'Lease end date must be on or after the start date.';
    const rent = Number(formData.weeklyRent);
    if (!Number.isFinite(rent) || rent <= 0) nextErrors.weeklyRent = 'Rent amount must be greater than zero.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    onSubmit({
      propertyId: currentLease?.propertyId || '',
      spaceId: currentLease?.spaceId || '',
      tenantId: tenant?.id || currentLease?.tenantId || '',
      startDate: toTimestamp(formData.startDate),
      endDate: toTimestamp(formData.endDate),
      weeklyRent: Number(formData.weeklyRent),
      rentBasis: formData.rentBasis || 'weekly',
      paymentFrequency: formData.paymentFrequency || 'weekly',
      rentInAdvance: Number(currentLease?.rentInAdvance || 0),
      bondAmount: Number(currentLease?.bondAmount || 0),
      bondWeeks: Number(currentLease?.bondWeeks || 0),
      bondAuthority: currentLease?.bondAuthority || '',
      bondReference: currentLease?.bondReference || '',
      bondLodgementDate: currentLease?.bondLodgementDate || null,
      bondStatus: currentLease?.bondStatusValue || currentLease?.bondStatus || 'not_lodged',
      leaseType: currentLease?.leaseTypeValue || currentLease?.leaseType || 'residential',
      status: 'active',
      rentReviewDate: null,
      rentReviewType: currentLease?.rentReviewTypeValue || currentLease?.rentReviewType || 'fixed_percent',
      rentIncreaseHistory: [],
      notes: formData.notes || ''
    });
  };

  const inputClass = 'min-h-[44px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500';
  const labelClass = 'mb-1 block text-xs font-medium text-slate-600';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <div className="font-semibold">Create a separate future lease</div>
        <p className="mt-1 text-xs leading-5 text-blue-800">
          The current lease remains unchanged. Occupancy and current rent will switch automatically when this new lease starts.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Next Lease Start *</label>
          <SmartDateInput name="startDate" value={formData.startDate} onChange={handleChange} className={inputClass} required />
          {errors.startDate && <p className="mt-1 text-xs text-red-600">{errors.startDate}</p>}
        </div>
        <div>
          <label className={labelClass}>Next Lease End *</label>
          <SmartDateInput name="endDate" value={formData.endDate} onChange={handleChange} className={inputClass} baseDate={formData.startDate} baseLabel="the next lease start date" required />
          {errors.endDate && <p className="mt-1 text-xs text-red-600">{errors.endDate}</p>}
        </div>
        <div>
          <label className={labelClass}>Rent Amount ($) *</label>
          <input type="number" name="weeklyRent" value={formData.weeklyRent} onChange={handleChange} className={inputClass} min="0.01" step="0.01" required />
          {errors.weeklyRent && <p className="mt-1 text-xs text-red-600">{errors.weeklyRent}</p>}
        </div>
        <div>
          <label className={labelClass}>Rent Basis</label>
          <select name="rentBasis" value={formData.rentBasis} onChange={handleChange} className={inputClass}>
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Payment Frequency</label>
          <select name="paymentFrequency" value={formData.paymentFrequency} onChange={handleChange} className={inputClass}>
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Renewal Notes</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} className={inputClass} rows="3" placeholder="Optional notes about the signed renewal..." />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} disabled={isSubmitting} className="min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="min-h-[44px] rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">{isSubmitting ? 'Saving Next Lease...' : 'Save Next Lease'}</button>
      </div>
    </form>
  );
};

export default LeaseRenewalForm;